// The operator console's language: a per-operator preference (operator_prefs, mirrored in
// localStorage so the first paint is already right), a switch in the topbar, and the hooks
// every component uses: useT() for text, useLocale() for dates, numbers and money.
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

import { request } from '../api/client.js';
import { intlLocale, isUiLang, makeT, makeTp, mergeDict, UI_LANGS, type Dict, type UiLang, type Vars } from '../../shared/i18n.js';

const STORAGE_KEY = 'signet.lang';

/** The catalogues — every page registers its own slice per language on load (<Page>.de.ts / <Page>.fr.ts). */
type CatalogLang = Exclude<UiLang, 'en'>;
const CATALOGS: Record<CatalogLang, Dict> = { de: {}, fr: {} };
export function registerCatalog(lang: CatalogLang, dict: Dict, source?: string): void { mergeDict(CATALOGS[lang], dict, source); }
export function registerDe(dict: Dict, source?: string): void { registerCatalog('de', dict, source); }
export function registerFr(dict: Dict, source?: string): void { registerCatalog('fr', dict, source); }

interface Locale {
  lang: UiLang;
  setLang: (l: UiLang) => void;
  t: (key: string, vars?: Vars) => string;
  tp: (n: number, one: string, other: string, vars?: Vars) => string;
  /** 2026-08-23 → 23.08.2026 (de) / 23/08/2026 (en); null → '—'. */
  fmtDate: (iso: string | null | undefined) => string;
  fmtDateTime: (iso: string | null | undefined) => string;
  fmtNum: (n: number | string | null | undefined, digits?: number) => string;
  fmtMoney: (amount: number | string | null | undefined, currency?: string | null) => string;
}

const fallbackT = makeT(null);
const LocaleContext = createContext<Locale>({
  lang: 'en', setLang: () => {}, t: fallbackT, tp: makeTp(fallbackT),
  fmtDate: (s) => (s ? s.slice(0, 10) : '—'), fmtDateTime: (s) => s ?? '—', fmtNum: (n) => String(n ?? '—'), fmtMoney: (a) => String(a ?? '—'),
});

function initialLang(): UiLang {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (isUiLang(stored)) return stored;
  } catch { /* private mode */ }
  const nav = typeof navigator !== 'undefined' ? navigator.language.slice(0, 2) : 'en';
  return isUiLang(nav) ? nav : 'en';
}

export function LocaleProvider({ children, authed = false }: { children: ReactNode; authed?: boolean }) {
  const [lang, setLangState] = useState<UiLang>(initialLang);

  // the stored preference wins once the operator is known; the localStorage copy keeps the first paint right
  useEffect(() => {
    if (!authed) return;
    request<{ ui_language: string }>('/me/prefs')
      .then((p) => { if (isUiLang(p.ui_language)) setLangState(p.ui_language); })
      .catch(() => null);
  }, [authed]);
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, lang); } catch { /* ignore */ }
    document.documentElement.setAttribute('lang', lang);
  }, [lang]);

  const setLang = useCallback((l: UiLang) => {
    setLangState(l);
    if (authed) void request('/me/prefs', { method: 'PUT', body: JSON.stringify({ ui_language: l }) }).catch(() => null);
  }, [authed]);

  const value = useMemo<Locale>(() => {
    const t = makeT(lang === 'en' ? null : CATALOGS[lang]);
    const loc = intlLocale(lang);
    const dateF = new Intl.DateTimeFormat(loc, { day: '2-digit', month: '2-digit', year: 'numeric' });
    const dateTimeF = new Intl.DateTimeFormat(loc, { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    const parse = (iso: string): Date | null => {
      const d = /^\d{4}-\d{2}-\d{2}$/.test(iso) ? new Date(`${iso}T00:00:00`) : new Date(iso);
      return Number.isNaN(d.getTime()) ? null : d;
    };
    return {
      lang, setLang, t, tp: makeTp(t),
      fmtDate: (iso) => { if (!iso) return '—'; const d = parse(iso); return d ? dateF.format(d) : iso; },
      fmtDateTime: (iso) => { if (!iso) return '—'; const d = parse(iso); return d ? dateTimeF.format(d) : iso; },
      fmtNum: (n, digits = 0) => (n === null || n === undefined || n === '' ? '—'
        : new Intl.NumberFormat(loc, { minimumFractionDigits: digits, maximumFractionDigits: digits }).format(Number(n))),
      fmtMoney: (a, currency) => (a === null || a === undefined || a === '' ? '—'
        : `${currency ?? 'CHF'} ${new Intl.NumberFormat(loc, { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(Number(a))}`),
    };
  }, [lang, setLang]);

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export const useLocale = (): Locale => useContext(LocaleContext);
export const useT = (): Locale['t'] => useContext(LocaleContext).t;

/** The DE | EN switch — one control, in the topbar beside the theme toggle. */
export function LangSwitch() {
  const { lang, setLang } = useLocale();
  return (
    <span className="lang-switch" role="group" aria-label="Language">
      {UI_LANGS.map((l) => (
        <button
          key={l}
          type="button"
          className={`lang-switch-btn${lang === l ? ' on' : ''}`}
          aria-pressed={lang === l}
          onClick={() => setLang(l)}
        >{l.toUpperCase()}</button>
      ))}
    </span>
  );
}
